import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUserSession } from 'amazon-cognito-identity-js';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import { DateTime } from 'luxon'

import { getImpersonationAccess, getMe, getOAuthToken, getOrgByUser } from '../../app/utils/Users';
import Settings from '../../app/utils/Settings'
import { appendModel, getSitesAsync, orgChange, tokenChange } from '../site/siteSlice';
import { setMetricsFromSessionStorage } from '../metric/metricSlice';
import history from '../../history';
import { getVersion } from '../../app/utils/common';
import { getAgentTokens, getOrgSalesToken } from '../../app/utils/Sites';

const initialState = {
  adminSession: null,
  impersonationStatus: "idle",
  isAuthenticated: false,
  showAdminScreen: true,
  useDarkMode: false,
  token: null,
  tokenResults: null,
  user: null,
  error: null,
  granted: false,
  isAdmin: false,
  trialMode: false,
  trialExpired: false,
  showSidebar: true,
  showHeader: true,
  onboardingInProgress: false,
  oauthTokens: {},
  redirectPathName: null,
  useInteractiveGuideline: true,
  clientVersion: null,
  disableGA4Override: false,
  ga4MetricDateOverride: DateTime.local().startOf("month").toISODate(),
  onboarding: {
    step: 1,
    metricsLoaded: false,
    loadingStatsProgress: 5,
    animationClass: 'service-selection',
    socialMediaService: "gmb",
    paidAdsService: "bing",
    dontHaveIt: {
      fbPage: false,
      linkedinOrg: false,
      gmb: false,
      fb: false,
      linkedin: false,
      bing: false
    },
    videoEnded: false,
    startupStats: {
      progress: 0
    },
    qAndA: {
      q1: {
        question: "What CRM do you use?",
        answer: ""
      },
      q2: {
        question: "Do you use either SMS or AI driven lead qualifier solutions? If so, what platform?",
        answer: ""
      },
      q3: {
        question: "Do you have last years franchise development unit sales & revenue for import into your dashboard?",
        answer: ""
      },
      q4: {
        question: "Would you like our platform to provide a top ten keyword score for your free trial?",
        answer: ""
      },
      q5: {
        question: "Are you a member of VetFran (we have a great discount for members)?",
        answer: ""
      },
      q6: {
        question: "Are you interested in adding your brand site to your dashboard free trial?",
        answer: ""
      }
    }
  }
};

export const userCheckAsync = createAsyncThunk(
  'session/userCheck',
  async ({email, password}) => {
    try {

      const user = await Auth.signIn(email.toLowerCase(), password);
      if(user.challengeName){
        setChallenge(user)
        return {user}
      } else {
        setStatus("loading")
        const {response, error} = await getOrgByUser(user.signInUserSession.idToken.jwtToken)
        let granted = true
        const result = {user, granted}

        if(error || !response){
          granted = false
        }

        // if(response){
        //   const { site, override } = response
        //   granted = override

        //   if(!granted){
        //     let origin = "https://engine.internetstrategylabs.com"
        //     if(site.attr?.origin !== undefined){
        //         origin = site.attr.origin
        //     }
            
        //     granted = origin === Settings.origin
        //   }
        if(granted && import.meta.env.MODE === "production" && Settings.origin === Settings.oldOrigin){
          granted = false
        }
        result.granted = granted

        if(granted){
          result.token = user.signInUserSession.idToken.jwtToken
        }
        // }

        return result
      }
    } catch(e) {
      return {error: e}
    }
  }
);

export const setImpersonationSession = createAsyncThunk(
  'session/impersonate',
  async(email, thunkAPI) => {
    const currentSession = await Auth.currentSession()

    window.localStorage.setItem("adminSession", JSON.stringify(currentSession))
    // await thunkAPI.dispatch(setAdminSession(currentSession))

    const {response, error} = await getImpersonationAccess(email)

    if(response){
      window.localStorage.setItem("token", response.result.IdToken)
      
      const impersonatedSession = new CognitoUserSession({
        IdToken: new CognitoIdToken({
          IdToken: response.result.IdToken
        }),
        AccessToken: new CognitoAccessToken({
          AccessToken: response.result.AccessToken
        }),
        RefreshToken: new CognitoRefreshToken({
          RefreshToken: response.result.RefreshToken
        })
      })

      Auth.currentSession = async() => impersonatedSession

      return response.result
    } else {
      toast.error("Something went wrong", {containerId: "defaultToastContainer"})

      return error
    }
  })

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    authenticate: (state, action) => {
      state.isAuthenticated = true;
      const res = JSON.parse(atob(action.payload.split('.')[1]))

      state.token = action.payload
      state.tokenResults = res
      state.isAdmin = (res["cognito:groups"]||[]).includes("Administrators")
      if(state.isAdmin){
        state.disableGA4Override = true
      }
    },
    setRedirectPathName: (state, action) => {
      state.redirectPathName = action.payload
    },
    setClientVersion: (state, action) => {
      state.clientVersion = action.payload
    },
    populateUser: (state, action) => {
      state.user = action.payload
    },
    setChallenge: (state, action) => {
      state.challenge = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.isAuthenticated = initialState.isAuthenticated
      state.token = initialState.token
    },
    setStatus: (state, action) => {
      state.status = action.payload
    },
    setTrialMode: (state, action) => {
      state.trialMode = action.payload
    },
    setTrialExpired: (state, action) => {
      state.trialExpired = action.payload
    },
    setAdminScreen: (state, action) => {
      state.showAdminScreen = action.payload
    },
    toggleSidebar: (state, action) => {
      state.showSidebar = action.payload
    },
    toggleHeader: (state, action) => {
      state.showHeader = action.payload
    },
    setInitOAuthToken: (state, action) => {
      Object.keys(action.payload).forEach(tk => {
        state.oauthTokens[tk] = action.payload[tk]
      })
    },
    setOAuthToken: (state, action) => {
      state.oauthTokens[action.payload.type] = action.payload.id
    },
    setOnboardingInProgress: (state, action) => {
      state.onboardingInProgress = action.payload
    },
    appendOnboarding: (state, action) => {
      state.onboarding = {
        ...state.onboarding,
        ...action.payload
      }
    },
    setDisableGA4Override:(state, action) => {
      state.disableGA4Override = action.payload
    },
    toggleDarkMode: (state, action) => {
      state.useDarkMode = action.payload
    },
    setMetricscLoaded: (state, action) => {
      state.onboarding.metricsLoaded = action
    },
    setAdminSession: (state, action) => {
      state.adminSession = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // .addCase(userCheckAsync.pending, (state) => {
      //   state.status = 'loading';
      // })
      .addCase(userCheckAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload.user ?? initialState.user
        state.error = action.payload.error ?? initialState.error
        state.granted = action.payload.granted ?? initialState.granted
        state.token = action.payload.token ?? initialState.token

        state.useInteractiveGuideline = window.innerWidth >= 992
      })
      .addCase(setImpersonationSession.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(setImpersonationSession.fulfilled, (state, action) => {
        // state.status = 'idle'
        window.location.href = "/"
      })
      .addCase(setImpersonationSession.rejected, (state, action) => {
        state.status = 'idle'
        state.adminSession = null
      })
  },
});

export const isAuthenticated = (state) => state.session.isAuthenticated;
export const isGranted = (state) => state.session.granted;

export const parseGa4Accounts = (state) => {
  if(state.session?.oauthTokens?.ga?.id && state.session?.oauthTokens?.ga?.id.includes("ga4:")){
    return [...(state.session?.oauthTokens?.ga?.accounts ?? [])]
      .sort((a,b) => a.displayName < b.displayName ? -1 : a.displayName > b.displayName ? 1 : 0)
      .slice(0)
  }

  return []
}


export const { 
  authenticate, 
  setRedirectPathName, 
  populateUser, 
  setError, 
  setStatus, 
  setTrialMode, 
  setTrialExpired,
  setChallenge, 
  setAdminScreen, 
  toggleSidebar,
  toggleHeader, 
  setInitOAuthToken, 
  setOAuthToken,
  setClientVersion,
  setOnboardingInProgress,
  appendOnboarding,
  setDisableGA4Override, 
  toggleDarkMode,
  setMetricscLoaded,
  setAdminSession
} = sessionSlice.actions;

export const getClientVersion = () => async(dispatch, getState) => {
  try {
    const data = await getVersion().then(({response, error}) => {
      if(error){
        throw error
      }
      return response
    })
    await dispatch(setClientVersion(data.version))
    return data
  } catch(e){
    throw e
  }
}

export const getStartupProgress = () => async(dispatch, getState) => {
  try {
    const state = getState()

    const { model } = state.site
    const site = model?.sites[model?.siteActiveIdx]
    const brand = model?.organizations[model?.orgActiveIdx]
    const { user } =  state.session

    const socialMediaClaimed = site?.attr?.accounts?.fbPage && 
        site?.attr?.accounts?.gmb &&
        site?.attr?.accounts?.linkedinOrg
    
    const paidAdsClaimed = site?.attr?.accounts?.fb &&
        site?.attr?.accounts?.linkedin &&
        site?.attr?.accounts?.bing

    let progress = 0
    if(site?.attr?.accounts?.ga){
      progress += 30
    }

    if(user?.avatar){
      progress += 15
    }

    if(brand?.attr?.logo){
      progress += 15
    }

    if(socialMediaClaimed){
      progress += 20
    }

    if(paidAdsClaimed){
      progress += 20
    }

    await dispatch(appendOnboarding({
      startupStats: {
        ...state.session.onboarding.startupStats,
        progress
      }
    }))
  } catch(e){
    throw e
  }
}

export const switchDarkMode = (darkMode) => async(dispatch, getState) => {
  try {
    await dispatch(toggleDarkMode(darkMode))
    
    if(darkMode){
      document.body.classList.add('darkMode');
      window.localStorage.setItem("useDarkMode", true)
    } else {
      document.body.classList.remove('darkMode');
      window.localStorage.removeItem("useDarkMode")
    }
  } catch(e){
    throw e
  }
}

export const fetchUserData = (token) => async(dispatch, getState) => {
  try {
    const data = await getMe(token).then(({response, error}) => {
      if(error){
        throw error
      }
      return response
    })

    return data
  } catch(e){
    throw e
  }
}

export const fetchOAuthData = (token) => async(dispatch, getState) => {
  let oauthTokens = null
  try {
    oauthTokens = JSON.parse(localStorage.getItem('oauthTokens'))
  } catch(e) { /* do nothing */ }

  if(oauthTokens){

    try {
      let tokenData = {}
      let loaders = Object.keys(oauthTokens||{}).map(type => {
        return getOAuthToken(token, oauthTokens[type])
          .then(({response}) => {            
            tokenData[type] = {
              id: oauthTokens[type],
              accounts: response.result
            }
          })
      })

      return Promise.all(loaders).then(() => tokenData)
    } catch(e){
      throw e
    }
  } else {
    return {}
  }
}

export const darkModeCheck = () => async(dispatch, getState) => {
  let state = getState()

  if(state.useDarkMode || window.localStorage.getItem("useDarkMode")){
    document.body.classList.add('darkMode');
    dispatch(toggleDarkMode(true))
  } else {
    document.body.classList.remove('darkMode');
    dispatch(toggleDarkMode(false))
  }
}

export const initAuthData = (loading=false) => async(dispatch, getState) => {
  let state = getState()
  const urlParams = new URLSearchParams(window.location.search);
  
  if(loading){
    // await dispatch(sessionDataRedirect())
    await dispatch(setStatus("loading"))
  }
  
  if(window.location?.href.includes("/verify")){
    localStorage.removeItem('token')

    if(loading){
      await dispatch(setStatus("idle"))
    }
    return
  }

  if(window.localStorage.getItem("adminSession")){
    const session = JSON.parse(window.localStorage.getItem("adminSession"))
    await dispatch(setAdminSession(session))
  }

	const token = state.session.token || localStorage.getItem('token')

	if(!token){
    if(loading){
      await dispatch(setStatus("idle"))
    }
		return
	}
  try {
    await dispatch(getClientVersion())


    const results = await dispatch(fetchUserData(token))

    const authRes = await dispatch(authenticate(token))
    localStorage.setItem("token", token)

    const resp = await dispatch(getSitesAsync(results))
    await dispatch(populateUser(results))
    // await dispatch(setMetricsFromSessionStorage())

    if(urlParams.has("tokenId")){
      const tokenId = urlParams.get("tokenId")

      let oauthTokens = {}
      try {
        oauthTokens = JSON.parse(localStorage.getItem('oauthTokens'))
        if(oauthTokens === null){
          oauthTokens = {}
        }
      } catch(e) { oauthTokens = {} }

      if (tokenId.startsWith('fbPage:')) {
        oauthTokens['fbPage'] = tokenId
      } else if (tokenId.startsWith('fb:')) {
        oauthTokens['fb'] = tokenId
      } else if (tokenId.startsWith('gmb:')) {
        oauthTokens['gmb'] = tokenId
      } else if (tokenId.startsWith('bing:')) {
        oauthTokens['bing'] = tokenId
      } else if (tokenId.startsWith('gsc:')) {
        oauthTokens['gsc'] = tokenId
      } else if (tokenId.startsWith('linkedinOrg:')) {
        oauthTokens['linkedinOrg'] = tokenId
      } else if (tokenId.startsWith('linkedin:')) {
        oauthTokens['linkedin'] = tokenId
      } else {
        oauthTokens['ga'] = tokenId
      }
      
      localStorage.setItem('oauthTokens', JSON.stringify(oauthTokens))

      const authTokens = await dispatch(fetchOAuthData(token))
      await dispatch(setInitOAuthToken(authTokens))

      if(loading){
        await dispatch(setStatus("initialized"))
      }

      if(urlParams.has("state")){
        if(resp?.payload?.sites?.length > 0){
          let { orgFixed } = resp.payload
          await dispatch(orgChange(orgFixed ? "0" : -1))
        }
        return history.push(urlParams.get("state"))
      }      
    } else { 
      const authTokens = await dispatch(fetchOAuthData(token))
      await dispatch(setInitOAuthToken(authTokens))

      if(loading){
        await dispatch(setStatus("initialized"))
      }


      if(state.session.redirectPathName){
        history.push(state.session.redirectPathName)
        await dispatch(removeRedirect())
      }
    }

    state = getState()
    
    if(state.session.isAdmin){
      await dispatch(setDisableGA4Override(true))
    }
    
    const { sites, organizations, orgFixed, tokenFixed, reseller } = resp.payload
    
    if(tokenFixed && reseller){
      await dispatch(tokenChange(tokenFixed ? "0" : -1))
    }
    
    if(sites?.length > 0){
      const trialExpiryDate = DateTime.fromISO(sites[0].attr?.trialExpiryDate)
      const diff = trialExpiryDate.diffNow()
      let trialMode = false

      if(sites.length === 1 && sites[0].attr.trialExpiryDate){
        trialMode = true
      }

      if(orgFixed &&
        organizations[0].attr?.token){
        await dispatch(getSalesTokenByOrg(organizations[0].siteId))        
      }

      await dispatch(setTrialMode(trialMode))

      // if ((!singleSiteUser || singleSiteUser === undefined) || sites[0].attr.trialExpiryDate === undefined || (sites[0].attr.trialExpiryDate && diff.milliseconds > 0)) {
      await dispatch(orgChange(orgFixed ? "0" : -1))
      // }

      state = getState()

      if(loading){
        await dispatch(setStatus("initialized"))
      }

      if(!state.session.isAdmin && state.session.trialMode && diff.milliseconds <= 0 && !history.location.pathname.includes("success")
        && !history.location.pathname.includes("checkout")){
        await dispatch(toggleSidebar(false))
        await dispatch(setTrialExpired(true))
        
        return history.push('/trial-expired')
      }
      else if(sites){
        if(!history.location.pathname.includes("onboarding") && sites[0].attr?.status && 
          sites[0].siteId === state.session.user.siteId &&
          sites[0].attr?.status === "evaluation") {
            await dispatch(toggleSidebar(false))
            
            return history.push('/initial-scan')
          }
        if(sites[0].siteId === state.session.user.siteId && 
          sites[0].attr?.status === "onboarding" &&
          !window.sessionStorage.getItem("onboarded")){
          await dispatch(toggleSidebar(false))
    
          return history.push('/onboarding')
        }

        if(trialMode){
          await dispatch(getStartupProgress())
        }
      }
    }

  } catch(e){
    console.log(e)
    await dispatch(setError())
    if(loading){
      await dispatch(setStatus("idle"))
    }
  }
}

export const sessionDataRedirect = (override) => async(dispatch, getState) => {
  await dispatch(setRedirectPathName(override ?? history.location.pathname))
  if(!override){
    sessionStorage.setItem("redirect", history.location.pathname)
  }
}

export const removeRedirect = () => async(dispatch, getState) => {
  await dispatch(setRedirectPathName(initialState.redirectPathName))
  sessionStorage.removeItem("redirect")
}

export const getSalesTokenByOrg = (siteId) => async(dispatch, getState) => {
  const state = getState()
  const model = state.site.model
  
  const data = await getOrgSalesToken(siteId).then(({response, error}) => {
    if(error){
      return {}
    }
    return response
  })

  if(data?.salesToken){
    await dispatch(appendModel({
      salesTokens: [...model.salesTokens, {...data?.salesToken}]
    }))
  }
}

export const revertToAdminSession = () => async(dispatch, getState) => {
  const state = getState()
  
  Auth.currentSession = async() => state.session.adminSession
  window.localStorage.removeItem("adminSession")
  window.localStorage.setItem("token", state.session.adminSession.idToken.jwtToken)
  window.location.reload(true)

}

export default sessionSlice.reducer;
